import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, LogIn, Eye, EyeOff, CheckCircle2, Loader2, Store, ArrowRight, ShoppingBag, User, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';

export default function ShopifyConnect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, login, register, logout } = useAuth();
  
  // URL params
  const shopDomain = searchParams.get('shop') || '';
  const action = searchParams.get('action') || '';
  const chargeId = searchParams.get('charge_id') || '';
  
  // Shopify subscription state
  const [shopifyEntitlement, setShopifyEntitlement] = useState(null);
  
  // Step management: 'auth' | 'verify-email' | 'select-brand' | 'connecting' | 'success'
  const [step, setStep] = useState('auth');
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState('');
  const [resendStatus, setResendStatus] = useState({ loading: false, message: '', error: '' });
  const [authMode, setAuthMode] = useState(action === 'register' ? 'register' : 'login');
  
  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [productUpdatesOptIn, setProductUpdatesOptIn] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  
  // Brands state
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [isCreateBrandOpen, setIsCreateBrandOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [currency, setCurrency] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [isCreatingBrand, setIsCreatingBrand] = useState(false);
  
  // Connection state
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Check Shopify entitlement if charge_id exists
  useEffect(() => {
    if (shopDomain && chargeId) {
      console.log('[SHOPIFY] Checking entitlement for:', shopDomain);
      fetch(`https://api.glowytics.com/api/auth/shopify/entitlement?shop=${encodeURIComponent(shopDomain)}`)
        .then(res => res.json())
        .then(data => {
          console.log('[SHOPIFY] Entitlement result:', data);
          setShopifyEntitlement(data);
        })
        .catch(err => console.error('[SHOPIFY] Entitlement check failed:', err));
    }
  }, [shopDomain, chargeId]);

  // Check if user is already logged in
  useEffect(() => {
    if (user) {
      setStep('select-brand');
      fetchBrands();
    }
  }, [user]);

  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/brands', {
        credentials: 'include'
      });
      const data = await response.json();
      setBrands(data.brands || []);
    } catch (error) {
      console.error('Fetch brands error:', error);
      toast.error('Failed to fetch brands');
    } finally {
      setLoadingBrands(false);
    }
  };

  const performLogin = async (loginEmail, loginPassword) => {
    try {
      const response = await fetch('https://api.glowytics.com/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 403 && data.code === 'EMAIL_NOT_VERIFIED') {
          return { success: false, needsVerification: true, error: data.error || 'Please verify your email before signing in.' };
        }
        return { success: false, error: data.message || data.error || 'Invalid credentials' };
      }
      login(data.user);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      const result = await performLogin(email, password);
      if (result.success) {
        setStep('select-brand');
        fetchBrands();
      } else if (result.needsVerification) {
        setPendingVerifyEmail(email);
        setStep('verify-email');
      } else {
        setFormError(result.error || 'Invalid credentials');
      }
    } catch (error) {
      setFormError('Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.glowytics.com/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ full_name: name, email, password, source: 'shopify', productUpdatesOptIn, marketingOptIn })
      });
      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        const text = await response.text().catch(() => '');
        data = { error: text || `HTTP ${response.status}` };
      }
      
      if (response.ok) {
        toast.success('Account created — check your email to verify.');
        setPendingVerifyEmail(email);
        setStep('verify-email');
      } else {
        console.error('Register failed:', response.status, data);
        setFormError(data.message || data.error || `Registration failed (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setFormError(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConnectBrand = async (brand) => {
    if (!shopDomain) {
      toast.error('Shop domain is missing');
      return;
    }

    setSelectedBrand(brand);
    setIsConnecting(true);
    setStep('connecting');

    try {
      const brandId = brand.id;

      // Save brandId to Shopify app DB via connect route, which then redirects
      // to Shopify Admin to complete the connection inside the embedded app
      const connectUrl = `https://apps.glowytics.com/connect?shop=${encodeURIComponent(shopDomain)}&brandId=${encodeURIComponent(brandId)}`;

      // Link the brand to the shop as its owner. The backend only accepts the store's
      // token for brands linked here, so this step is required.
      const linkRes = await fetch('https://api.glowytics.com/api/auth/shopify/link-brand', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: shopDomain, brandId })
      });
      if (!linkRes.ok) {
        const err = await linkRes.json().catch(() => ({}));
        throw new Error(err.error || 'Could not link this brand to your store');
      }

      window.location.href = connectUrl;
    } catch (error) {
      console.error('Connection error:', error);
      toast.error(error.message || 'Failed to start connection');
      setIsConnecting(false);
      setStep('select-brand');
    }
  };

  const resetCreateBrandForm = () => {
    setNewBrandName('');
    setWebsiteUrl('');
    setIndustry('');
    setTimeZone('');
    setCurrency('');
    setLogoUrl('');
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    if (!newBrandName.trim()) {
      toast.error('Please enter a brand name');
      return;
    }

    setIsCreatingBrand(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/brands', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newBrandName.trim(),
          websiteUrl,
          industry,
          timeZone,
          currency,
          logoUrl
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create brand');
      }

      // If coming from Shopify with subscription, link the brand
      if (shopDomain && chargeId && data.brand?.id) {
        console.log('[SHOPIFY] Linking brand to subscription:', shopDomain, data.brand.id);
        try {
          const linkResponse = await fetch('https://api.glowytics.com/api/auth/shopify/link-brand', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ shop: shopDomain, brandId: data.brand.id })
          });
          if (linkResponse.ok) {
            console.log('[SHOPIFY] Brand linked to subscription');
            toast.success('Brand created and linked to Shopify subscription!');
          } else {
            console.error('[SHOPIFY] Failed to link brand');
            toast.success('Brand created successfully');
          }
        } catch (linkError) {
          console.error('[SHOPIFY] Link error:', linkError);
          toast.success('Brand created successfully');
        }
      } else {
        toast.success('Brand created successfully');
      }

      setBrands((prev) => [data.brand, ...prev]);
      setIsCreateBrandOpen(false);
      resetCreateBrandForm();
    } catch (error) {
      console.error('Create brand error:', error);
      toast.error(error.message || 'Failed to create brand');
    } finally {
      setIsCreatingBrand(false);
    }
  };

  // Glowytics Logo
  const GlowyticsLogo = ({ size = 40 }) => (
    <img 
      src="/Calisma-Yuzeyi-20@5x-4.png" 
      alt="Glowytics" 
      style={{ width: size, height: 'auto', objectFit: 'contain' }}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <GlowyticsLogo size={60} />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Connect Shopify Store</h1>
          {shopDomain && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full">
              <ShoppingBag className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700 font-medium">{shopDomain}</span>
            </div>
          )}
          {/* Shopify Subscription Banner */}
          {shopifyEntitlement?.hasEntitlement && (
            <div className="mt-4 mx-auto max-w-sm p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700 font-medium">
                🎉 Subscription Active: {shopifyEntitlement.subscription?.planName}
              </p>
              <p className="text-xs text-green-600 mt-1">
                Create or select a brand to complete setup
              </p>
            </div>
          )}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          
          {/* Auth Step (Login/Register) */}
          {step === 'auth' && (
            <div className="p-6">
              {/* Tab Switch */}
              <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => { setAuthMode('login'); setFormError(''); }}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                    authMode === 'login' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => { setAuthMode('register'); setFormError(''); }}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                    authMode === 'register' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Create Account
                </button>
              </div>

              <form onSubmit={authMode === 'login' ? handleLogin : handleRegister} className="space-y-4">
                {/* Name field - only for register */}
                {authMode === 'register' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        placeholder="John Doe"
                        required
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="••••••••"
                      required
                      minLength={authMode === 'register' ? 6 : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {authMode === 'register' && (
                  <div className="space-y-2 pt-1">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={productUpdatesOptIn}
                        onChange={(e) => setProductUpdatesOptIn(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      />
                      <span className="text-xs text-gray-600">
                        Send me product updates, weekly reports, and tips to get the most out of Glowytics.
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={marketingOptIn}
                        onChange={(e) => setMarketingOptIn(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      />
                      <span className="text-xs text-gray-600">
                        I'd like to receive promotional emails about new features, offers, and events. You can unsubscribe at any time.
                      </span>
                    </label>
                  </div>
                )}

                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{formError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-2.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {authMode === 'login' ? 'Signing in...' : 'Creating account...'}
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      {authMode === 'login' ? 'Sign In' : 'Create Account'}
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 1b: Verify Email */}
          {step === 'verify-email' && (
            <div className="p-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-5">
                <Mail className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Verify your email</h2>
              <p className="text-sm text-gray-600 mb-1">We sent a verification link to</p>
              <p className="text-sm font-medium text-gray-900 mb-4 break-all">{pendingVerifyEmail}</p>
              <p className="text-xs text-gray-500 mb-6">
                Click the link in your email to activate your account, then come back here and sign in to continue connecting your Shopify store. The link expires in 24 hours.
              </p>

              {resendStatus.message && (
                <div className="mb-3 p-2.5 bg-green-50 border border-green-200 rounded-md text-xs text-green-700">
                  {resendStatus.message}
                </div>
              )}
              {resendStatus.error && (
                <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">
                  {resendStatus.error}
                </div>
              )}

              <button
                type="button"
                disabled={resendStatus.loading || !pendingVerifyEmail}
                onClick={async () => {
                  setResendStatus({ loading: true, message: '', error: '' });
                  try {
                    const r = await fetch('https://api.glowytics.com/api/auth/resend-verification', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: pendingVerifyEmail })
                    });
                    const d = await r.json().catch(() => ({}));
                    if (!r.ok) {
                      setResendStatus({ loading: false, message: '', error: d.error || 'Could not resend' });
                      return;
                    }
                    setResendStatus({ loading: false, message: 'Verification email sent. Check your inbox.', error: '' });
                  } catch (e) {
                    setResendStatus({ loading: false, message: '', error: e.message || 'Network error' });
                  }
                }}
                className="w-full py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 mb-2"
              >
                {resendStatus.loading ? 'Sending…' : 'Resend verification email'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep('auth');
                  setAuthMode('login');
                  setFormError('');
                  setResendStatus({ loading: false, message: '', error: '' });
                }}
                className="w-full py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Already verified? Sign in
              </button>
            </div>
          )}

          {/* Step 2: Select Brand */}
          {step === 'select-brand' && (
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                <h2 className="text-lg font-medium text-gray-900">Select a Brand</h2>
                <p className="text-sm text-gray-500 mt-1">Choose which brand to connect with your store</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setIsCreateBrandOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-full text-sm font-medium shadow-sm hover:bg-gray-800 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Create Brand
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const shopParam = shopDomain ? `shop=${encodeURIComponent(shopDomain)}` : '';
                      const redirectUrl = `/shopify-connect${shopParam ? `?${shopParam}&action=register` : '?action=register'}`;
                      logout(redirectUrl);
                    }}
                    className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Log out
                  </button>
                </div>
              </div>

              {loadingBrands ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : brands.length === 0 ? (
                <div className="text-center py-8">
                  <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">You don't have any brands yet</p>
                  <button
                    type="button"
                    onClick={() => setIsCreateBrandOpen(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-full font-medium shadow-sm hover:bg-gray-800 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Create Your First Brand
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {brands.map((brand) => (
                    <button
                      key={brand.id}
                      onClick={() => handleConnectBrand(brand)}
                      className="w-full p-4 border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all text-left group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            {brand.logoUrl ? (
                              <img src={brand.logoUrl} alt={brand.name} className="w-8 h-8 object-contain rounded" />
                            ) : (
                              <Store className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{brand.name}</p>
                            {brand.website && (
                              <p className="text-xs text-gray-500">{brand.website}</p>
                            )}
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Connecting */}
          {step === 'connecting' && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
              </div>
              <h2 className="text-lg font-medium text-gray-900">Connecting...</h2>
              <p className="text-sm text-gray-500 mt-2">
                Redirecting to Shopify to complete authorization
              </p>
              {selectedBrand && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
                  <Store className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-700">{selectedBrand.name}</span>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-lg font-medium text-gray-900">Connected Successfully!</h2>
              <p className="text-sm text-gray-500 mt-2">
                Your Shopify store is now connected to Glowytics
              </p>
              <button
                onClick={() => navigate('/settings?section=api-connections')}
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            By connecting, you agree to share store data with Glowytics for analytics purposes.
          </p>
        </div>
      </div>

      {isCreateBrandOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Create Brand</h3>
              <button
                type="button"
                onClick={() => {
                  setIsCreateBrandOpen(false);
                  resetCreateBrandForm();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateBrand} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name *</label>
                <input
                  type="text"
                  value={newBrandName}
                  onChange={(e) => setNewBrandName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="e.g. Glowytics Store"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                <input
                  type="text"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="example.com"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Select E-commerce Vertical</option>
                    <option value="Fashion & Apparel">Fashion & Apparel</option>
                    <option value="Electronics & Gadgets">Electronics & Gadgets</option>
                    <option value="Home & Garden">Home & Garden</option>
                    <option value="Health & Beauty">Health & Beauty</option>
                    <option value="Sports & Outdoors">Sports & Outdoors</option>
                    <option value="Toys & Hobbies">Toys & Hobbies</option>
                    <option value="Food & Beverage">Food & Beverage</option>
                    <option value="Subscription Boxes">Subscription Boxes</option>
                    <option value="Online Marketplace">Online Marketplace</option>
                    <option value="Digital Products & Services">Digital Products & Services</option>
                    <option value="Handmade & Artisan Goods">Handmade & Artisan Goods</option>
                    <option value="Dropshipping">Dropshipping</option>
                    <option value="Automotive">Automotive Parts & Accessories</option>
                    <option value="Pet Supplies">Pet Supplies</option>
                    <option value="Other E-commerce">Other E-commerce</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Select currency</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="TRY">TRY - Turkish Lira</option>
                    <option value="GBP">GBP - British Pound</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time Zone</label>
                  <select
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Select time zone</option>
                    <option value="UTC">UTC</option>
                    <option value="Europe/Istanbul">Europe/Istanbul</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="America/New_York">America/New York</option>
                    <option value="America/Los_Angeles">America/Los Angeles</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand Logo URL</label>
                  <input
                    type="url"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateBrandOpen(false);
                    resetCreateBrandForm();
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingBrand}
                  className="px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingBrand ? 'Creating...' : 'Create Brand'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
